import { AzureChatSettings } from "@/types/ChatSettings";
import AzureChatSettingsForm from "./AzureChatSettingsForm";
import { forwardRef, useState, useImperativeHandle, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OllamaChatSettingsForm from "./OllamaChatSettingsForm";
import JunetChatSettingsForm from "./JunetChatSettingsForm";
import VLLMChatSettingsForm from "./VLLMChatSettingsForm";
import { chatProviderMap, ChatProvider } from "@shared/types/providers/chat-provider";
import OpenRouterChatSettingsForm from "./OpenRouterChatSettingsForm";
import OpenAIChatSettingsForm from "./OpenAIChatSettingsForm";
import AnthropicChatSettingsForm from "./AnthropicChatSettingsForm";

type Props = {
    data?: AzureChatSettings;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
};

const ChatProviderForm = forwardRef(({ data, initialData = {}, onDataChange, onSubmit, ...props }: Props, ref) => {
    const [chatProvider, setChatProvider] = useState<ChatProvider | string>(
        initialData?.CHAT_PROVIDER || data?.CHAT_PROVIDER || "azure"
    );

    const formRef: any = useRef(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        validateForm: async () => {
            if (formRef.current && formRef.current.validateForm) {
                return await formRef.current.validateForm();
            }
            return true;
        },
        getFormData: () => {
            const displayNameInput = document.getElementById("display-name") as HTMLInputElement;
            const providerData = {
                CHAT_PROVIDER: chatProvider,
                display_name: displayNameInput?.value || "",
            };
            if (formRef.current && formRef.current.getFormData) {
                return {
                    ...providerData,
                    ...formRef.current.getFormData(),
                };
            }
            return providerData;
        },
        submitForm: () => {
            if (formRef.current && formRef.current.submitForm && onSubmit) {
                formRef.current.submitForm();
            }
        },
    }));

    // Update parent component when chat provider changes (removed useEffect to prevent infinite rerenders)

    const handleDataChange = (formData: any) => {
        if (onDataChange) {
            const displayNameInput = document.getElementById("display-name") as HTMLInputElement;
            onDataChange({
                ...formData,
                CHAT_PROVIDER: chatProvider,
                display_name: displayNameInput?.value || "",
            });
        }
    };

    const handleSubmit = (_data: any) => {
        if (!onSubmit) return;

        const displayNameInput = document.getElementById("display-name") as HTMLInputElement;
        const __data = {
            ...data,
            ..._data,
            CHAT_PROVIDER: chatProvider,
            display_name: displayNameInput?.value || "",
        };

        onSubmit(__data);
    };

    const renderForm = (chatProvider: ChatProvider) => {
        const formProps = {
            ...props,
            onSubmit: handleSubmit,
            data: {
                ...data,
                ...initialData,
            },
            onDataChange: handleDataChange,
        };

        switch (chatProvider) {
            case "azure":
                return <AzureChatSettingsForm {...formProps} ref={formRef} />;
            case "openai":
                return <OpenAIChatSettingsForm {...formProps} ref={formRef} />;
            case "anthropic":
                return <AnthropicChatSettingsForm {...formProps} ref={formRef} />;
            case "ollama":
                return <OllamaChatSettingsForm {...formProps} ref={formRef} />;
            case "vllm":
                return <VLLMChatSettingsForm {...formProps} ref={formRef} />;
            case "junet":
                return <JunetChatSettingsForm {...formProps} ref={formRef} />;
            case "openrouter":
                return <OpenRouterChatSettingsForm {...formProps} ref={formRef} />;
            default:
                return <AzureChatSettingsForm {...formProps} ref={formRef} />;
        }
    };

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="display-name">Display Name</Label>
                <Input
                    id="display-name"
                    type="text"
                    defaultValue={initialData?.display_name || ""}
                    onChange={(e) => {
                        if (onDataChange) {
                            onDataChange({
                                ...initialData,
                                display_name: e.target.value,
                                CHAT_PROVIDER: chatProvider,
                            });
                        }
                    }}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="chat-provider">Provider</Label>
                <Select
                    defaultValue={chatProvider}
                    onValueChange={(value) => {
                        setChatProvider(value);
                        if (onDataChange) {
                            const displayNameInput = document.getElementById("display-name") as HTMLInputElement;
                            onDataChange({
                                ...initialData,
                                CHAT_PROVIDER: value,
                                display_name: displayNameInput?.value || "",
                            });
                        }
                    }}
                >
                    <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select Chat" id="chat-provider" />
                    </SelectTrigger>
                    <SelectContent>
                        {Object.values(chatProviderMap).map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                                <div className="flex items-center gap-2">
                                    <img src={item.image} alt={item.label} width="20" height="20" />
                                    {item.label}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            {renderForm(chatProvider as ChatProvider)}
        </div>
    );
});

ChatProviderForm.displayName = "ChatProviderForm";

export default ChatProviderForm;
